/**
 * 路由配置
 * 文档： https://router.vuejs.org/zh/introduction.html
 */
import { createRouter, createWebHashHistory } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useVideoCallStore } from '@/stores/videoCall'
import { useGlobalStore } from '@/stores/global'
import { t } from '@/locales'
import { NAV_TYPES } from '@/common/constants/navTypes'
import { getMergedMeta } from '@/utils/route'
import { showLoginModal } from '@/components/LoginModal/functionCall'
import { until } from '@vueuse/core'

import { showToast } from 'vant'

// 为什么设置 titleKey 而不是 title？
// 因为 titleKey 是多语言的，随语言变化，而 title 是固定的，所以需要设置 titleKey
const router = createRouter({
  history: createWebHashHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (e.g., browser back/forward), restore it
    if (savedPosition) {
      return savedPosition
    }
    // Otherwise, scroll to top
    return { top: 0 }
  },
  routes: [
    {
      path: '/',
      component: () => import('@/layouts/AppLayout.vue'),
      children: [
        // 首页
        {
          name: 'home',
          path: '',
          component: () => import('@/views/home/HomePage.vue'),
          meta: {
            titleKey: 'nav.home',
            nav: NAV_TYPES.MAIN,
            tab: true,
          },
        },
        // 消息相关页面
        {
          path: 'messages',
          meta: {
            requiresAuth: true,
          },
          children: [
            // 父级路由容器（负责上报逻辑）
            {
              path: '',
              component: () => import('@/views/messages/MessagesLayout.vue'),
              meta: {
                titleKey: 'nav.messages',
                nav: NAV_TYPES.MESSAGE,
                tab: true,
              },
              redirect: 'normal',
              children: [
                // 会话列表（默认子路由）
                {
                  path: 'normal',
                  name: 'messages-normal',
                  // alias: 'list', // 支持 /messages/list 访问
                  component: () => import('@/views/messages/NormalMessagesPage.vue'),
                },
                // 关注列表
                {
                  path: 'follows',
                  name: 'messages-follows',
                  component: () => import('@/views/messages/FollowsPage.vue'),
                },
              ],
            },
            // 陌生人会话列表（独立路由，不上报）
            {
              path: 'stranger',
              name: 'messages-stranger',
              component: () => import('@/views/messages/StrangerMessagesPage.vue'),
              meta: {
                titleKey: 'nav.strangerMessages',
                nav: NAV_TYPES.COMMON,
              },
            },
          ],
        },
        // 个人主页
        {
          name: 'profile',
          path: 'profile',
          component: () => import('@/views/profile/ProfilePage.vue'),
          meta: {
            titleKey: 'nav.profile',
            nav: NAV_TYPES.MAIN,
            tab: true,
            requiresAuth: true,
          },
        },
        // 用户详情页
        {
          path: 'user/:userId',
          name: 'user-detail',
          props: true,
          component: () => import('@/views/user/UserDetailPage.vue'),
          meta: {
            titleKey: 'nav.userDetail',
            requiresAuth: true,
          },
        },
        // 视频通话页面
        {
          path: 'video/:roomId',
          name: 'video',
          props: true,
          component: () => import('@/views/video/VideoCallPage.vue'),
          beforeEnter: (to, from, next) => {
            if (!useVideoCallStore().isConnected) {
              showToast(t('videoCall.missingRoomInfo'))
              window.history.back()
              return
            }
            next()
          },
          meta: {
            titleKey: 'nav.video',
            requiresAuth: true,
          },
        },
        // 1v1聊天页面
        {
          path: 'chat/:conversationId',
          name: 'chat',
          component: () => import('@/views/chat/ChatPage.vue'),
          props: true,
          meta: {
            titleKey: 'nav.chat',
            nav: NAV_TYPES.CHAT,
            requiresAuth: true,
          },
        },
        // 商品相关页面
        {
          path: 'product',
          meta: {
            requiresAuth: true,
          },
          children: [
            // VIP订阅页面
            {
              path: 'vip',
              name: 'vip',
              component: () => import('@/views/product/VipPage.vue'),
              meta: {
                titleKey: 'nav.vip',
                nav: NAV_TYPES.VIP,
              },
            },
            // 钻石充值页面
            {
              path: 'diamond',
              name: 'diamond',
              component: () => import('@/views/product/DiamondPage.vue'),
              meta: {
                titleKey: 'nav.diamond',
                nav: NAV_TYPES.DIAMOND,
              },
            },
          ],
        },
        // 支付相关页面
        {
          path: 'payment',
          children: [
            // 支付成功页面
            {
              path: 'success',
              name: 'payment-success',
              component: () => import('@/views/payment/PaymentSuccessPage.vue'),
              meta: {
                titleKey: 'payment.success',
              },
            },
            // 支付失败页面
            {
              path: 'failure',
              name: 'payment-failure',
              component: () => import('@/views/payment/PaymentFailurePage.vue'),
              meta: {
                titleKey: 'payment.failure',
              },
            },
          ],
        },
        // 协议相关页面（保持路径不变，仅代码组织）
        // 隐私政策页面
        {
          path: 'privacy-policy',
          name: 'privacy-policy',
          component: () => import('@/views/protocol/PrivacyPolicy/PrivacyPolicy.vue'),
          meta: {
            titleKey: 'profile.privacyPolicy',
            nav: NAV_TYPES.COMMON,
          },
        },
        // 使用条款页面
        {
          path: 'terms-of-use',
          name: 'terms-of-use',
          component: () => import('@/views/protocol/TermsOfUse/TermsOfUse.vue'),
          meta: {
            titleKey: 'profile.termsOfUse',
            nav: NAV_TYPES.COMMON,
          },
        },
        // 社区准则页面
        {
          path: 'community-guidelines',
          name: 'community-guidelines',
          component: () => import('@/views/protocol/CommunityGuidelines/CommunityGuidelines.vue'),
          meta: {
            titleKey: 'profile.communityGuidelines',
            nav: NAV_TYPES.COMMON,
          },
        },
        // 账号相关页面
        {
          path: 'account',
          meta: {
            requiresAuth: true,
            nav: NAV_TYPES.COMMON,
          },
          children: [
            // 账号管理页面
            {
              path: 'management',
              name: 'account-management',
              component: () => import('@/views/account/AccountManagementPage.vue'),
              meta: {
                titleKey: 'account.accountManagement',
              },
            },
            // 设置密码页面
            {
              path: 'set-password',
              name: 'account-set-password',
              component: () => import('@/views/account/SetPasswordPage.vue'),
              meta: {
                titleKey: 'account.setPassword',
              },
            },
            // 账号注销页面
            {
              path: 'cancellation',
              name: 'account-cancellation',
              component: () => import('@/views/account/AccountCancellationPage.vue'),
              meta: {
                titleKey: 'account.accountCancellation',
              },
            },
          ],
        },
        {
          path: 'oauth',
          children: [
            // Google OAuth 回调页面
            {
              path: 'google-callback',
              name: 'oauth-google-callback',
              component: () => import('@/views/oauth/GoogleCallbackPage.vue'),
              meta: {
                titleKey: 'login.processing',
              },
            },
            // Apple OAuth 回调页面
            {
              path: 'apple-callback',
              name: 'oauth-apple-callback',
              component: () => import('@/views/oauth/AppleCallbackPage.vue'),
              meta: {
                titleKey: 'login.processing',
              },
            },
          ]
        }
      ],
    },
    // 开发者工具警告页面（静态导入，确保生产环境可用）
    // {
    //   path: '/devtool-warning',
    //   name: 'devtool-warning',
    //   component: DevToolWarningPage,
    //   meta: {
    //     titleKey: 'error.devtool.title',
    //   },
    // },
    // 404 页面
    {
      path: '/:pathMatch(.*)*',
      name: 'notFound',
      component: () => import('@/views/error/NotFoundPage.vue'),
      meta: {
        titleKey: 'error.notFound.title',
      },
    },
  ],
})

router.beforeEach(async (to, _from, next) => {
  const globalStore = useGlobalStore()
  const userStore = useUserStore()
  const mergedMeta = getMergedMeta(to)

  // 设置页面标题（支持多语言）
  if (mergedMeta.titleKey) {
    document.title = t(mergedMeta.titleKey)
  }

  // 如果已登录，直接跳转
  if (userStore.isLoggedIn) {
    next()
    return
  }

  // 否则权限验证，不需要权限的页面直接跳转
  if (!mergedMeta.requiresAuth) {
    next()
    return
  }

  /**
   * 方案1:
   * 等待用户状态初始化完成后再跳转（避免初始化时的状态闪烁）
   * 缺点：页面呈现慢
   */
  // if (globalStore.userStateInitializing) {
  //   // 在初始化
  //   showLoadingToast({
  //     message: t('common.initializing'),
  //     forbidClick: true,
  //     duration: 0, // 持续显示直到手动关闭
  //   })

  //   await until(() => globalStore.userStateInitializing).toBe(false)

  //   // 等待响应式更新完全传播
  //   await nextTick()

  //   closeToast()
  //   if (!userStore.isLoggedIn) {
  //     next({
  //       path: '/',
  //     })
  //     showLoginModal({
  //       source: 'router-guard',
  //       redirect: to.fullPath
  //     })
  //   } else {
  //     next()
  //   }
  // } else {
  //   // 如果未在初始化，跳转首页并显示登录弹窗
  //   next({
  //     path: '/',
  //   })
  //   showLoginModal({
  //     source: 'router-guard',
  //     redirect: to.fullPath
  //   })
  // }

  /**
   * 方案2:
   * 需要权限的页面，不管三七二十一，先跳转首页，再去检查登录状态，未登录则显示登录弹窗
   */
  next({
    path: '/',
  })
  if (globalStore.userStateInitializing) {
    await until(() => globalStore.userStateInitializing).toBe(false)
  }

  // 初始化完成或者没有在初始化状态，检查登录状态
  if (!userStore.isLoggedIn) {
    showLoginModal({
      source: 'router-guard',
      redirect: to.fullPath
    })
  }
})

export default router
